import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import AgentDealDetailModal from "../../components/AgentDealDetailModal";
import DealSubmissionModal from "../../components/DealSubmissionModal";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Building2, Search, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import { Input } from "../../components/ui/input";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending Review" },
  { value: "changes_requested", label: "Changes Requested" },
  { value: "approved", label: "Approved / Live" },
  { value: "under_contract", label: "Under Contract" },
  { value: "closed", label: "Closed" },
  { value: "rejected", label: "Rejected" },
];

const AgentPropertiesPage = () => {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [editingDeal, setEditingDeal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const perPage = 10;

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchDeals = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const params = { page, per_page: perPage };
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/deals`, { withCredentials: true, headers, params });
      const data = res.data;
      setDeals(data.deals || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals();
  }, [page, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const formatCurrency = (value) => {
    if (!value) return "$0";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const getPropertyTypeLabel = (type) => {
    const labels = { single_family: "Single Family", multi_family: "Multi-Family", apartment: "Apartment", commercial: "Commercial", land: "Land", mixed_use: "Mixed Use" };
    return labels[type] || type;
  };

  const getStatusStyle = (status) => {
    const map = {
      pending: "bg-orange-500/10 text-orange-600 border-orange-200",
      active: "bg-green-500/10 text-green-600 border-green-200",
      approved: "bg-blue-500/10 text-blue-600 border-blue-200",
      reserved: "bg-purple-500/10 text-purple-600 border-purple-200",
      in_closing: "bg-indigo-500/10 text-indigo-600 border-indigo-200",
      closed: "bg-gray-500/10 text-gray-600 border-gray-200",
      rejected: "bg-red-500/10 text-red-600 border-red-200",
      changes_requested: "bg-yellow-500/10 text-yellow-700 border-yellow-300",
    };
    return map[status] || "bg-muted text-muted-foreground";
  };

  const getStatusLabel = (status) => {
    const map = { active: "Live", approved: "Approved", reserved: "Reserved", in_closing: "In Closing", changes_requested: "Changes Requested" };
    return map[status] || status?.charAt(0).toUpperCase() + status?.slice(1);
  };

  const filteredDeals = searchQuery
    ? deals.filter(d =>
        d.property_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.state?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : deals;

  return (
    <DashboardLayout title="My Properties">
      <div className="space-y-6 animate-fade-in" data-testid="agent-properties-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">My Properties</h1>
            <p className="text-muted-foreground mt-1">{total} total propert{total === 1 ? "y" : "ies"}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by address, city, state..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="properties-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUS_FILTERS.map((f) => (
              <Button
                key={f.value}
                variant={statusFilter === f.value ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(f.value)}
                className={statusFilter === f.value ? "bg-blue-500 hover:bg-blue-600" : ""}
                data-testid={`filter-${f.value || "all"}`}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Deals Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-5 bg-muted rounded w-3/4 mb-3" />
                  <div className="h-4 bg-muted rounded w-1/2 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredDeals.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-lg font-medium text-muted-foreground">No properties found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {statusFilter ? "Try changing the filter" : "Submit your first deal to get started"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredDeals.map((deal) => (
              <Card
                key={deal.deal_id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedDeal(deal)}
                data-testid={`property-card-${deal.deal_id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{deal.property_address}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {deal.city}, {deal.state} {deal.zip_code}
                      </p>
                    </div>
                    <Badge variant="outline" className={`shrink-0 ml-2 ${getStatusStyle(deal.status)}`}>
                      {getStatusLabel(deal.status)}
                    </Badge>
                  </div>
                  {(deal.status === "changes_requested" || deal.status === "rejected") && deal.admin_notes && (
                    <div className={`flex items-start gap-2 p-2.5 rounded-md mb-3 text-sm ${deal.status === "changes_requested" ? "bg-yellow-50 text-yellow-700" : "bg-red-50 text-red-700"}`}>
                      <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span className="line-clamp-2">{deal.admin_notes}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-semibold text-foreground">{formatCurrency(deal.asking_price)}</span>
                    <span className="text-muted-foreground">{getPropertyTypeLabel(deal.property_type)}</span>
                    {deal.bedrooms && <span className="text-muted-foreground">{deal.bedrooms} bed</span>}
                    {deal.bathrooms && <span className="text-muted-foreground">{deal.bathrooms} bath</span>}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span>ARV: {formatCurrency(deal.estimated_arv)}</span>
                    <span>Rehab: {formatCurrency(deal.estimated_rehab_cost)}</span>
                    {deal.wholesale_fee > 0 && <span>Fee: {formatCurrency(deal.wholesale_fee)}</span>}
                  </div>
                  {deal.reserved_by_name && (
                    <p className="text-xs text-purple-600 mt-2">Reserved by: {deal.reserved_by_name}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 pt-4" data-testid="properties-pagination">
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      <AgentDealDetailModal
        open={!!selectedDeal}
        onOpenChange={(open) => { if (!open) setSelectedDeal(null); }}
        deal={selectedDeal}
        onEdit={(deal) => {
          setEditingDeal(deal);
          setShowEditModal(true);
        }}
      />
      <DealSubmissionModal
        open={showEditModal}
        onOpenChange={(open) => {
          setShowEditModal(open);
          if (!open) setEditingDeal(null);
        }}
        onSuccess={fetchDeals}
        draftData={editingDeal}
      />
    </DashboardLayout>
  );
};

export default AgentPropertiesPage;
